/**
 * @file TranslationMapsAccordion.js
 * @description This file contains the TranslationMapsAccordion component, which displays the translation maps or formula map based on the selected map type.
 */

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import OperatorDropdown from '../shared/asyncDropdowns/OperatorDropdown';
import { getText } from '../../i18n';

const TranslationMapsAccordion = ({
    group,
    index,
    templateData,
    setTemplateData,
    selectedTemplateType,
    setSelectedTemplateType,
}) => {
    if (!selectedTemplateType) {
        return null;
    }

    const translationMapInputRefs = useRef([]);
    const formulaMapInputRefs = useRef([]);

    useEffect(() => {
        translationMapInputRefs.current = translationMapInputRefs.current.slice(
            0,
            group.translationMaps?.length,
        );
        formulaMapInputRefs.current = formulaMapInputRefs.current.slice(
            0,
            group.formulaMap?.evaluations?.length,
        );
    }, [group.translationMaps, group.formulaMap]);

    useEffect(() => {
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];

        if (group.importMapGroupTypeId === 1) {
            if ((group.translationMaps || []).length === 0) {
                updatedGroups[index] = {
                    ...updatedGroups[index],
                    translationMaps: [
                        {
                            id: 0,
                            processingOrder: 1,
                            from: '',
                            to: '',
                            else: false,
                        },
                        {
                            id: 0,
                            processingOrder: 2,
                            from: null,
                            to: null,
                            else: true,
                        },
                    ],
                };

                setSelectedTemplateType(prevType => ({
                    ...prevType,
                    importMapGroups: updatedGroups,
                }));
                return;
            }

            const elseMap = (group.translationMaps || []).find(
                translationMap => translationMap.else,
            );

            if (!elseMap) {
                const newElseMap = {
                    id: 0,
                    processingOrder: (group.translationMaps || []).length + 1,
                    from: null,
                    to: null,
                    else: true,
                };

                updatedGroups[index] = {
                    ...updatedGroups[index],
                    translationMaps: [
                        ...(group.translationMaps || []),
                        newElseMap,
                    ],
                };

                setSelectedTemplateType(prevType => ({
                    ...prevType,
                    importMapGroups: updatedGroups,
                }));
                return;
            }
        }

        if (
            group.importMapGroupTypeId === 2 &&
            (group.formulaMap?.evaluations || []).length === 0
        ) {
            updatedGroups[index] = {
                ...updatedGroups[index],
                formulaMap: {
                    ...updatedGroups[index].formulaMap,
                    value: '',
                    evaluations: [
                        {
                            id: 0,
                            leftExpression: '',
                            rightExpression: '',
                            operatorId: null,
                        },
                    ],
                },
            };

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));
        }
    }, [
        group.importMapGroupTypeId,
        group.translationMaps,
        group.formulaMap,
        index,
        selectedTemplateType,
        setSelectedTemplateType,
    ]);

    const handleInputChange = (event, mapIndex) => {
        const { name, value } = event.target;
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedMaps = [...(updatedGroups[index].translationMaps || [])];

        updatedMaps[mapIndex] = {
            ...updatedMaps[mapIndex],
            [name]: value,
        };

        updatedGroups[index] = {
            ...updatedGroups[index],
            translationMaps: updatedMaps,
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleFormulaInputChange = (event, evaluationIndex) => {
        const { name, value } = event.target;
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedEvaluations = [
            ...(updatedGroups[index].formulaMap?.evaluations || []),
        ];

        updatedEvaluations[evaluationIndex] = {
            ...updatedEvaluations[evaluationIndex],
            [name]: value,
        };

        updatedGroups[index] = {
            ...updatedGroups[index],
            formulaMap: {
                ...updatedGroups[index].formulaMap,
                evaluations: updatedEvaluations,
            },
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleOperatorChange = (selectedOperator, evaluationIndex) => {
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedEvaluations = [
            ...(updatedGroups[index].formulaMap?.evaluations || []),
        ];

        updatedEvaluations[evaluationIndex] = {
            ...updatedEvaluations[evaluationIndex],
            operatorId: selectedOperator?.id || null,
            operatorName: selectedOperator?.name || '',
        };

        updatedGroups[index] = {
            ...updatedGroups[index],
            formulaMap: {
                ...updatedGroups[index].formulaMap,
                evaluations: updatedEvaluations,
            },
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleTranslationMapKeyPress = (event, mapIndex) => {
        if (event.key === 'Enter') {
            const updatedGroups = [
                ...(selectedTemplateType.importMapGroups || []),
            ];
            const updatedMaps = [
                ...(updatedGroups[index].translationMaps || []),
            ];
            const elseIndex = updatedMaps.findIndex(map => map.else);

            if (elseIndex !== -1) {
                const elseMap = updatedMaps[elseIndex];
                elseMap.processingOrder = updatedMaps.length + 1;

                updatedMaps.splice(elseIndex, 0, {
                    id: 0,
                    processingOrder: elseIndex + 1,
                    from: '',
                    to: '',
                    else: false,
                });
            } else {
                updatedMaps.push({
                    id: 0,
                    processingOrder: updatedMaps.length + 1,
                    from: '',
                    to: '',
                    else: false,
                });
            }

            updatedGroups[index] = {
                ...updatedGroups[index],
                translationMaps: updatedMaps,
            };

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));

            setTimeout(() => {
                const newRowIndex =
                    elseIndex !== -1 ? elseIndex : updatedMaps.length - 2;
                translationMapInputRefs.current[newRowIndex]?.focus();
            }, 0);
        }
    };

    const handleFormulaMapKeyPress = event => {
        if (event.key === 'Enter') {
            const updatedGroups = [
                ...(selectedTemplateType.importMapGroups || []),
            ];
            const updatedEvaluations = [
                ...(updatedGroups[index].formulaMap?.evaluations || []),
            ];

            updatedEvaluations.push({
                id: 0,
                leftExpression: '',
                rightExpression: '',
                operatorId: null,
            });

            updatedGroups[index] = {
                ...updatedGroups[index],
                formulaMap: {
                    ...updatedGroups[index].formulaMap,
                    evaluations: updatedEvaluations,
                },
            };

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));

            setTimeout(() => {
                const newRowIndex = updatedEvaluations.length - 1;
                formulaMapInputRefs.current[newRowIndex]?.focus();
            }, 0);
        }
    };

    const handleDeleteTranslationMap = mapIndex => {
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedMaps = [...(updatedGroups[index].translationMaps || [])];

        updatedMaps.splice(mapIndex, 1);

        const sortedMaps = [...updatedMaps].sort(
            (a, b) => a.processingOrder - b.processingOrder,
        );

        let processingOrder = 1;
        sortedMaps.forEach(map => {
            map.processingOrder = processingOrder;
            processingOrder++;
        });

        updatedGroups[index] = {
            ...updatedGroups[index],
            translationMaps: sortedMaps,
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleDeleteFormulaMap = evaluationIndex => {
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedEvaluations = [
            ...(updatedGroups[index].formulaMap?.evaluations || []),
        ];

        updatedEvaluations.splice(evaluationIndex, 1);

        updatedGroups[index] = {
            ...updatedGroups[index],
            formulaMap: {
                ...updatedGroups[index].formulaMap,
                evaluations: updatedEvaluations,
            },
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleFormulaValueChange = event => {
        const { value } = event.target;
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];

        updatedGroups[index] = {
            ...updatedGroups[index],
            formulaMap: {
                ...updatedGroups[index].formulaMap,
                value,
            },
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleMoveMapUp = evaluationIndex => {
        if (evaluationIndex > 0) {
            const updatedGroups = [
                ...(selectedTemplateType.importMapGroups || []),
            ];
            const updatedTranslationMaps = [
                ...(updatedGroups[index].translationMaps || []),
            ];

            const temp = updatedTranslationMaps[evaluationIndex];
            updatedTranslationMaps[evaluationIndex] =
                updatedTranslationMaps[evaluationIndex - 1];
            updatedTranslationMaps[evaluationIndex - 1] = temp;

            updatedTranslationMaps.forEach((map, i) => {
                map.processingOrder = i + 1;
            });

            updatedGroups[index] = {
                ...updatedGroups[index],
                translationMaps: updatedTranslationMaps,
            };

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));
        }
    };

    const handleMoveMapDown = evaluationIndex => {
        const updatedGroups = [...(selectedTemplateType.importMapGroups || [])];
        const updatedTranslationMaps = [
            ...(updatedGroups[index].translationMaps || []),
        ];

        if (evaluationIndex < updatedTranslationMaps.length - 2) {
            const temp = updatedTranslationMaps[evaluationIndex];
            updatedTranslationMaps[evaluationIndex] =
                updatedTranslationMaps[evaluationIndex + 1];
            updatedTranslationMaps[evaluationIndex + 1] = temp;

            updatedTranslationMaps.forEach((map, i) => {
                map.processingOrder = i + 1;
            });

            updatedGroups[index] = {
                ...updatedGroups[index],
                translationMaps: updatedTranslationMaps,
            };

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));
        }
    };

    return (
        <div className="ms-5 mb-5">
            <div className="accordion-body">
                {group.importMapGroupTypeId === 1 && (
                    <div className="row">
                        <div className="col-md-5 mb-2">
                            {getText('msg_3f66052a107e')}
                        </div>
                        <div className="col-md-5 mb-2">
                            {getText('msg_ae79ea1e9c63')}
                        </div>
                        <div className="col-md-1 mb-2"></div>
                        {(group.translationMaps || []).map((map, mapIndex) => (
                            <React.Fragment key={`${map.id}-${mapIndex}`}>
                                <div className="row mb-2">
                                    <div className="col-md-5">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={
                                                map.else
                                                    ? getText(
                                                          'msg_bf4cc46f7f14',
                                                      )
                                                    : map.from || ''
                                            }
                                            onChange={event =>
                                                handleInputChange(
                                                    event,
                                                    mapIndex,
                                                )
                                            }
                                            onKeyPress={event =>
                                                handleTranslationMapKeyPress(
                                                    event,
                                                    mapIndex,
                                                )
                                            }
                                            name="from"
                                            ref={el =>
                                                (translationMapInputRefs.current[
                                                    mapIndex
                                                ] = el)
                                            }
                                            readOnly={map.else}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={map.to || ''}
                                            onChange={event =>
                                                handleInputChange(
                                                    event,
                                                    mapIndex,
                                                )
                                            }
                                            onKeyPress={event =>
                                                handleTranslationMapKeyPress(
                                                    event,
                                                    mapIndex,
                                                )
                                            }
                                            name="to"
                                        />
                                    </div>
                                    <div className="d-flex col-1">
                                        {!map.else && (
                                            <>
                                                <i
                                                    className="bi bi-trash3 me-2 text-danger mx-1 align-content-center pointer-cursor"
                                                    onClick={() =>
                                                        handleDeleteTranslationMap(
                                                            mapIndex,
                                                        )
                                                    }
                                                ></i>
                                                {mapIndex > 0 && (
                                                    <button
                                                        className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                                        onClick={() =>
                                                            handleMoveMapUp(
                                                                mapIndex,
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_2038bdec9210',
                                                        )}
                                                    </button>
                                                )}
                                                {mapIndex <
                                                    group.translationMaps
                                                        .length -
                                                        2 && (
                                                    <button
                                                        className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                                        onClick={() =>
                                                            handleMoveMapDown(
                                                                mapIndex,
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_bf93e5ce8bc1',
                                                        )}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {group.importMapGroupTypeId === 2 && (
                    <div className="row">
                        <div className="col-md-3 mb-2">
                            {getText('msg_2d278a919dca')}
                        </div>
                        <div className="col-md-4 mb-2">
                            {getText('msg_d0e687b079fb')}
                        </div>
                        <div className="col-md-4 mb-2">
                            {getText('msg_dc86d3ecc841')}
                        </div>
                        <div className="col-md-1"></div>
                        {(group.formulaMap?.evaluations || []).map(
                            (evaluation, evaluationIndex) => (
                                <React.Fragment
                                    key={`${evaluation.id}-${evaluationIndex}`}
                                >
                                    <div className="row mb-2">
                                        <div className="col-md-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={
                                                    evaluation.leftExpression ||
                                                    ''
                                                }
                                                onChange={event =>
                                                    handleFormulaInputChange(
                                                        event,
                                                        evaluationIndex,
                                                    )
                                                }
                                                onKeyPress={
                                                    handleFormulaMapKeyPress
                                                }
                                                name="leftExpression"
                                                placeholder={getText(
                                                    'msg_2d278a919dca',
                                                )}
                                                ref={el =>
                                                    (formulaMapInputRefs.current[
                                                        evaluationIndex
                                                    ] = el)
                                                }
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <OperatorDropdown
                                                selectedOperator={
                                                    evaluation.operatorId
                                                        ? {
                                                              id: evaluation.operatorId,
                                                              name: '',
                                                          }
                                                        : null
                                                }
                                                onOperatorChange={selectedOperator =>
                                                    handleOperatorChange(
                                                        selectedOperator,
                                                        evaluationIndex,
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={
                                                    evaluation.rightExpression ||
                                                    ''
                                                }
                                                onChange={event =>
                                                    handleFormulaInputChange(
                                                        event,
                                                        evaluationIndex,
                                                    )
                                                }
                                                onKeyPress={
                                                    handleFormulaMapKeyPress
                                                }
                                                name="rightExpression"
                                                placeholder={getText(
                                                    'msg_dc86d3ecc841',
                                                )}
                                            />
                                        </div>
                                        <div className="d-flex col-1">
                                            <i
                                                className="bi bi-trash3 me-2 text-danger mx-1 align-content-center pointer-cursor"
                                                onClick={() =>
                                                    handleDeleteFormulaMap(
                                                        evaluationIndex,
                                                    )
                                                }
                                            ></i>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ),
                        )}
                        <div className="mb-2 d-flex">
                            <label className="align-content-center me-3">
                                {getText('msg_b7d93209feaa')}
                            </label>
                            <input
                                type="text"
                                className="form-control w-75"
                                value={group.formulaMap?.value || ''}
                                onChange={handleFormulaValueChange}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

TranslationMapsAccordion.propTypes = {
    group: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    templateData: PropTypes.object.isRequired,
    setTemplateData: PropTypes.func.isRequired,
    selectedTemplateType: PropTypes.object,
    setSelectedTemplateType: PropTypes.func.isRequired,
};

export default TranslationMapsAccordion;
